import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { PERMISSION } from 'src/features/auth';
import { useGetMonitoringHealthPayStation } from 'src/features/monitoring/api/useGetMonitoringHealthPayStation';
import { useGetPayStationStatus } from 'src/features/monitoring/api/useGetPayStationStatus';
import HealthIssuesHoverBadge, { HAS_ISSUES_STATUSES } from 'src/features/monitoring/components/HealthIssuesHoverBadge';
import { MonitoringHealthPayStation, MonitoringStatusCash } from 'src/features/monitoring/types';
import { MonitoringCurrentScreenMap } from 'src/features/monitoring/utils/constants';
import { parkingStatusMessages } from 'src/features/monitoring/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { payStationStageBadgeOptions, payStationTypeBadgeOptions } from 'src/features/payment/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import {
    GeneralBlock,
    GeneralBlockHead,
    GeneralBlockHeader,
    GeneralBlockSubtitle,
    GeneralBlockTitle,
} from 'src/registry/components/blocks/GeneralBlock';
import StatisticTable from 'src/registry/components/statistic/StatisticTable';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';

dayjs.extend(duration);

export default function SingleMonitoringPayStation({
    payStation,
    isOpen,
    isCashEnabled = true,
}: {
    payStation: MonitoringHealthPayStation;
    isOpen: boolean;
    isCashEnabled?: boolean;
}) {
    const { formatMessage, formatNumber } = useIntl();
    const settingsData = React.useContext(SettingsContext);

    const { parkingId } = useParking();
    const { data } = useGetPayStationStatus({ parkingId, payStationId: payStation.id });
    const ref = React.useRef<HTMLDivElement>(null);

    const [isIssuesHoverOpen, setIsIssuesHoverOpen] = React.useState(false);
    const showIssueCount = HAS_ISSUES_STATUSES.includes(payStation.status) && payStation.active_issue_count > 0;

    const { data: healthDetail, isLoading: isLoadingHealthDetail } = useGetMonitoringHealthPayStation({
        parkingId,
        payStationId: payStation.id,
        config: { enabled: showIssueCount && isIssuesHoverOpen },
    });

    const permissionAccessPayStation = useHasPermissions([PERMISSION.ACCESS_PAY_STATIONS]);

    React.useEffect(() => {
        if (!ref.current) return;

        if (isOpen) ref.current.style.height = `${ref.current.scrollHeight}px`;
        else ref.current.style.height = '0px';
    }, [isOpen, data, isCashEnabled]);

    const columns: ColumnDef<MonitoringStatusCash>[] = [
        {
            accessorKey: 'value',
            header: formatMessage(parkingStatusMessages.nominal),
            cell: ({ row: { original } }) =>
                formatNumber(original.value, {
                    style: 'currency',
                    currency: original.currency ?? settingsData.default_currency,
                }),
        },
        {
            accessorKey: 'count',
            header: formatMessage(parkingStatusMessages.count),
        },
        {
            accessorKey: 'total',
            header: formatMessage(parkingStatusMessages.total),
            cell: ({ row: { original } }) => (original.total / 100).toFixed(2),
        },
    ];

    const totalCashAmount = React.useMemo(() => {
        const totalAmount = data?.cash_levels?.total_amount;
        if (!totalAmount || !totalAmount.EUR) return '-';

        const [currency, total] = Object.entries(totalAmount)[0];

        return formatNumber(total / 100, {
            style: 'currency',
            currency: currency ?? settingsData.default_currency,
        });
    }, [data?.cash_levels?.total_amount, formatNumber, settingsData.default_currency]);

    const currentScreen = React.useMemo(() => {
        if (!data?.current_screen) return '-';
        const { current_screen } = data;

        const activeFrom = dayjs(current_screen.active_from);
        const timeElapsed = dayjs.duration(dayjs().diff(activeFrom)).format('HH:mm:ss');

        return `${formatMessage(
            MonitoringCurrentScreenMap[current_screen.screen] ?? {
                id: 'common.unknown',
                defaultMessage: current_screen.screen,
            },
        )} (${current_screen.language.toUpperCase()}) 
        - ${timeElapsed} ${formatMessage(parkingStatusMessages.timeElapsed).toLowerCase()}`;
    }, [data, formatMessage]);

    const currentOperationType = data?.current_operation?.type;
    const currentOperationStage = data?.current_operation?.stage;

    return (
        <div className="flex flex-col">
            <GeneralBlock className="flex-1 rounded-b-none border-b-0">
                <div className="flex w-full flex-row items-center justify-between">
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle className="text-md text-gray-900">{payStation?.name}</GeneralBlockTitle>
                            <GeneralBlockSubtitle className="text-sm">{payStation?.code}</GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <HealthIssuesHoverBadge
                        status={payStation.status}
                        activeIssueCount={payStation.active_issue_count}
                        isOpen={isIssuesHoverOpen}
                        onOpenChange={setIsIssuesHoverOpen}
                        isLoadingIssues={isLoadingHealthDetail}
                        issues={healthDetail?.active_issues}
                    />
                </div>
                <GeneralBlockHeader>
                    <GeneralBlockHead>
                        <GeneralBlockTitle>
                            <FormattedMessage {...parkingStatusMessages.currentOperation} />
                        </GeneralBlockTitle>
                        {currentOperationType && payStationTypeBadgeOptions[currentOperationType] ? (
                            <Badge
                                theme={payStationTypeBadgeOptions[currentOperationType].theme}
                                className="whitespace-break-spaces"
                            >
                                {formatMessage(payStationTypeBadgeOptions[currentOperationType].label)}
                                {' - '}
                                {currentOperationStage &&
                                    payStationStageBadgeOptions[currentOperationStage] &&
                                    formatMessage(
                                        payStationStageBadgeOptions[currentOperationStage]?.label,
                                    ).toLowerCase()}
                            </Badge>
                        ) : (
                            '-'
                        )}
                    </GeneralBlockHead>
                </GeneralBlockHeader>
                <GeneralBlockHeader>
                    <GeneralBlockHead>
                        <GeneralBlockTitle>
                            <FormattedMessage {...parkingStatusMessages.currentScreen} />
                        </GeneralBlockTitle>
                        <GeneralBlockSubtitle>{currentScreen}</GeneralBlockSubtitle>
                    </GeneralBlockHead>
                </GeneralBlockHeader>
                <GeneralBlockHeader>
                    <GeneralBlockHead>
                        <GeneralBlockTitle>
                            <FormattedMessage {...parkingStatusMessages.totalCashAmount} />
                        </GeneralBlockTitle>
                        <GeneralBlockSubtitle>{totalCashAmount}</GeneralBlockSubtitle>
                    </GeneralBlockHead>
                </GeneralBlockHeader>
                <div
                    className={cn('flex flex-col gap-4 overflow-hidden duration-500 ease-in-out', !isOpen && '-mt-4')}
                    ref={ref}
                >
                    {isCashEnabled && (
                        <>
                            <StatisticTable
                                title={formatMessage(parkingStatusMessages.banknotes)}
                                data={data?.cash_levels?.levels?.banknotes ?? []}
                                columns={columns as any}
                            >
                                <tr className="border-t">
                                    <th
                                        scope="row"
                                        className="px-4 py-2 text-start text-xs font-semibold text-gray-600"
                                    >
                                        <FormattedMessage {...parkingStatusMessages.total} />
                                    </th>
                                    <th scope="row" className="px-4 py-2 text-start text-xs font-normal">
                                        {data?.cash_levels?.levels?.total_banknotes?.count ?? '-'}
                                    </th>
                                    <th scope="row" className="px-4 py-2 text-start text-xs font-normal">
                                        {data?.cash_levels?.levels
                                            ? (data.cash_levels.levels?.total_banknotes?.amount / 100).toFixed(2)
                                            : '-'}
                                    </th>
                                </tr>
                            </StatisticTable>
                            <StatisticTable
                                title={formatMessage(parkingStatusMessages.coins)}
                                data={data?.cash_levels?.levels?.coins ?? []}
                                columns={columns as any}
                            >
                                <tr className="border-t">
                                    <th
                                        scope="row"
                                        className="px-4 py-2 text-start text-xs font-semibold text-gray-600"
                                    >
                                        <FormattedMessage {...parkingStatusMessages.total} />
                                    </th>
                                    <th scope="row" className="px-4 py-2 text-start text-xs font-normal">
                                        {data?.cash_levels?.levels?.total_coins?.count ?? '-'}
                                    </th>
                                    <th scope="row" className="px-4 py-2 text-start text-xs font-normal">
                                        {data?.cash_levels?.levels
                                            ? (data.cash_levels.levels?.total_coins?.amount / 100).toFixed(2)
                                            : '-'}
                                    </th>
                                </tr>
                            </StatisticTable>
                        </>
                    )}
                </div>
            </GeneralBlock>
            {permissionAccessPayStation && (
                <GeneralBlock className="rounded-t-none">
                    <Link to={`/${PATH.PAYMENTS}/pay-station/${payStation.id}`}>
                        <Button className="w-full">
                            <FontAwesomeIcon icon={ICON_MAP.ARROW_UP_RIGHT} />
                            <FormattedMessage {...parkingStatusMessages.openDashboard} />
                        </Button>
                    </Link>
                </GeneralBlock>
            )}
        </div>
    );
}
